import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/home/Footer';
import './About.css';

const About = () => {
  const pillars = [
    {
      title: 'Co-founder Discovery',
      description: 'Search and connect with potential co-founders who match your skills, interests, and startup goals.',
      icon: 'users'
    },
    {
      title: 'Talent & Recruitment',
      description: 'Find suitable candidates for open roles and recruit qualified people more efficiently.',
      icon: 'briefcase'
    },
    {
      title: 'Job Posting',
      description: 'Post hiring positions with clear plans: Basic (Free), Enhanced ($29), and Premium ($99).',
      icon: 'document'
    },
    {
      title: 'Investor Connections',
      description: 'Discover and connect with potential investors and relevant business opportunities.',
      icon: 'chart'
    },
    {
      title: 'Community Feed',
      description: 'Share ideas, seek advice, and build trust with founders and professionals before you collaborate.',
      icon: 'chat'
    },
    {
      title: 'Recruitment Support',
      description: 'Optional partner support to help companies identify, screen, and connect with suitable candidates.',
      icon: 'support'
    }
  ];

  const statements = [
    {
      label: 'Purpose',
      title: 'One platform for startup connections',
      body: 'CofounderHubs helps startups and entrepreneurs discover suitable co-founders, candidates, investors, and business opportunities in one place — so finding the right people and resources is easier and more efficient.'
    },
    {
      label: 'Vision',
      title: 'A trusted global startup hub',
      body: 'Our vision is to become a trusted global platform where startups, entrepreneurs, talented professionals, co-founders, and investors connect and build meaningful business opportunities together.'
    },
    {
      label: 'Mission',
      title: 'Simplify the startup ecosystem',
      body: 'Our mission is to bring founders, co-founders, candidates, investors, and companies together on one platform — reducing the time and difficulty of finding the right people and opportunities so startups can focus on building and growing.'
    }
  ];

  const milestones = [
    {
      year: '2026',
      title: 'Platform Foundation',
      description: 'CofounderHubs development begins — building a platform for startups to find co-founders, talent, and business opportunities.'
    },
    {
      year: '2026',
      title: 'Beta & Launch',
      description: 'Early startups help refine search, profiles, feed, and hiring flows. CofounderHubs launches with co-founder discovery, community, and job posting pathways.'
    },
    {
      year: '2027',
      title: 'Global Growth',
      description: 'Expanding reach and recruitment support so startups worldwide can connect with the right people more efficiently.'
    }
  ];

  const renderIcon = (type) => {
    const props = {
      width: 24,
      height: 24,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 1.75,
      strokeLinecap: 'round',
      strokeLinejoin: 'round'
    };

    switch (type) {
      case 'users':
        return (
          <svg {...props}>
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        );
      case 'briefcase':
        return (
          <svg {...props}>
            <rect x="2" y="7" width="20" height="14" rx="2" />
            <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
          </svg>
        );
      case 'document':
        return (
          <svg {...props}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
            <path d="M16 13H8" />
            <path d="M16 17H8" />
            <path d="M10 9H8" />
          </svg>
        );
      case 'chart':
        return (
          <svg {...props}>
            <path d="M18 20V10" />
            <path d="M12 20V4" />
            <path d="M6 20v-6" />
          </svg>
        );
      case 'chat':
        return (
          <svg {...props}>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        );
      case 'support':
        return (
          <svg {...props}>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="about-page">
      <section className="about-hero">
        <div className="about-hero-inner">
          <p className="about-eyebrow">About Us</p>
          <h1 className="about-hero-title">CofounderHubs</h1>
          <p className="about-hero-subtitle">
            Helping startups and entrepreneurs connect with the right people and resources —
            more easily and efficiently.
          </p>
          <div className="about-hero-actions">
            <Link to="/signup" className="about-btn about-btn-primary">Get Started</Link>
            <Link to="/contact" className="about-btn about-btn-secondary">Contact Us</Link>
          </div>
        </div>
      </section>

      <section className="about-section about-statements">
        <div className="about-container">
          <div className="about-statements-grid">
            {statements.map((item) => (
              <article key={item.label} className="about-statement-card">
                <span className="about-statement-label">{item.label}</span>
                <h2>{item.title}</h2>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section about-story">
        <div className="about-container about-story-layout">
          <div className="about-story-copy">
            <p className="about-eyebrow">What we do</p>
            <h2>Built for startups that need the right connections</h2>
            <p>
              Startups can search for co-founders, find candidates for open positions, post job
              opportunities, discover potential investors, and use CofounderHubs as a recruitment
              partner when they need help finding talent.
            </p>
            <p>
              Companies can post hiring positions with clear job posting plans — Basic (Free),
              Enhanced ($29), and Premium ($99). We can also provide recruitment support by helping
              companies identify, screen, and connect with suitable candidates for open roles.
            </p>
            <div className="about-story-links">
              <Link to="/search">Find co-founders</Link>
              <Link to="/startup-jobs">Browse jobs</Link>
              <Link to="/pricing">View pricing</Link>
            </div>
          </div>
          <div className="about-story-media">
            <img src="/img/business-handshake.jpg" alt="Professionals forming a business partnership" />
          </div>
        </div>
      </section>

      <section className="about-section about-pillars-section">
        <div className="about-container">
          <div className="about-section-header">
            <p className="about-eyebrow">Platform</p>
            <h2 className="section-title">How CofounderHubs helps</h2>
            <p className="about-section-lead">
              A networking, co-founder discovery, talent, job posting, and investor-connection
              platform — with recruitment support when needed.
            </p>
          </div>
          <div className="about-pillars-grid">
            {pillars.map((pillar) => (
              <article key={pillar.title} className="about-pillar-card">
                <div className="about-pillar-icon">{renderIcon(pillar.icon)}</div>
                <h3>{pillar.title}</h3>
                <p>{pillar.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section about-milestones-section">
        <div className="about-container">
          <div className="about-section-header">
            <p className="about-eyebrow">Journey</p>
            <h2 className="section-title">Where we are headed</h2>
          </div>
          <div className="milestones-timeline">
            {milestones.map((milestone) => (
              <div key={milestone.title} className="milestone-item">
                <div className="milestone-year">{milestone.year}</div>
                <div className="milestone-content">
                  <h3>{milestone.title}</h3>
                  <p>{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section about-cta-section">
        <div className="about-container about-cta-content">
          <h2>Ready to connect?</h2>
          <p>
            Join startups building teams, hiring talent, and discovering opportunities on
            CofounderHubs.
          </p>
          <div className="cta-buttons">
            <Link to="/signup" className="about-btn about-btn-primary">Get Started</Link>
            <Link to="/contact" className="about-btn about-btn-secondary">Contact Us</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
